import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { IonButton, IonIcon, IonList, IonItem, IonLabel, IonCheckbox, IonToggle } from "@ionic/react";
import {
  closeOutline, optionsOutline, createOutline, arrowBackOutline,
  mailUnreadOutline, mailOpenOutline, trashOutline,
} from "ionicons/icons";
import { confirmAlert } from "../utils/confirmAlert";
import { t } from "../utils/i18n";

// The bell drawer, shared by /app and the admin dashboard (the whodat panel):
//  - list mode: a muted "Notifications" sub-bar with an edit icon, rows that
//    open the item;
//  - select mode (edit icon): the sub-bar becomes a select-all checkbox plus
//    mark-unread / mark-read / delete / close, and every row gets a checkbox;
//  - preferences (sliders icon in the header): groups of Ionic toggles that
//    decide which notifications this device shows.
// Empty lists show the magnifier-with-x "No results found" state.

const iconBtnStyle = {
  "--color": "var(--ion-text-color)", "--border-radius": "50%",
  "--padding-start": "6px", "--padding-end": "6px", margin: 0, height: 40, minWidth: 40,
};

// `round`: the header's buttons take the shared round button (the bell's
// 48px circle and its 4% wash of the icon's colour on hover and while pressed)
function IconBtn({ icon, onClick, color, disabled, label, size = 22, className, round = false }) {
  return (
    <IonButton
      fill="clear"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={round ? `app-icon-btn app-sidebar-icon-btn ${className || ""}`.trim() : className}
      style={round ? (color ? { "--color": color } : undefined) : { ...iconBtnStyle, ...(color ? { "--color": color } : {}) }}
    >
      <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: size }}>
        <IonIcon icon={icon} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
      </span>
    </IonButton>
  );
}

// The magnifier-with-x empty state. `size="small"` is the compact version for
// inline spots like the topbar search panel.
export function NoResults({ text, size = "large", style }) {
  const small = size === "small";
  return (
    <div className={`notif-empty${small ? " notif-empty-small" : ""}`} style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", padding: small ? "22px 16px 24px" : "56px 24px 0", gap: small ? 10 : 18, color: "var(--ion-text-color)", ...style }}>
      <svg width={small ? 44 : 96} height={small ? 44 : 96} viewBox="0 0 96 96" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" aria-hidden="true">
        <circle cx="40" cy="40" r="27" />
        <line x1="60" y1="60" x2="86" y2="86" />
        <line x1="31" y1="31" x2="49" y2="49" />
        <line x1="49" y1="31" x2="31" y2="49" />
      </svg>
      <div style={{ fontSize: small ? "0.95rem" : "1.4rem", fontWeight: 700, textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>{text || t("No results found")}</div>
    </div>
  );
}

export default function NotificationsDrawer({
  open, onClose,
  items = [], renderItem, onOpenItem, onSetRead, onDelete,
  prefGroups = [], prefs = {}, onPrefChange,
  zIndex = 9995, background = "var(--app-sidebar-bg, #ffffff)", width = 340,
}) {
  const [mode, setMode] = useState("list"); // list | select | prefs
  const [selected, setSelected] = useState(() => new Set());

  // Closing the drawer always returns it to the plain list.
  useEffect(() => { if (!open) { setMode("list"); setSelected(new Set()); } }, [open]);

  const ids = useMemo(() => items.map((n) => n.id), [items]);
  const selectedIds = ids.filter((id) => selected.has(id));
  const selCount = selectedIds.length;
  const allSelected = ids.length > 0 && selCount === ids.length;

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(ids));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const exitSelect = () => { setMode("list"); setSelected(new Set()); };
  const withSelection = async (fn) => {
    if (!selCount) return;
    await fn(selectedIds);
    setSelected(new Set());
  };

  const header = (
    <div className="notif-header" style={{ display: "flex", alignItems: "center", padding: "env(safe-area-inset-top, 0px) 6px 0 16px", minHeight: "calc(60px + env(safe-area-inset-top, 0px))", flexShrink: 0, gap: 2, position: "relative", zIndex: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.14)" }}>
      <span style={{ flex: 1, fontSize: "1.15rem", fontWeight: 700, color: "var(--ion-text-color)" }}>{t("Notifications")}</span>
      {mode === "prefs"
        ? <IconBtn round className="notif-back" icon={arrowBackOutline} label={t("Back")} onClick={() => setMode("list")} size={24} />
        : <IconBtn round className="notif-prefs-btn" icon={optionsOutline} label={t("Preferences")} onClick={() => { setMode("prefs"); setSelected(new Set()); }} size={24} />}
      <IconBtn round className="notif-close" icon={closeOutline} label={t("Close")} onClick={onClose} size={24} />
    </div>
  );

  // Sub-bar: page background, with the same muted 0.72rem text the admin
  // table headers (Age / Customer / Document …) use.
  const subBarStyle = { display: "flex", alignItems: "center", minHeight: 52, padding: "0 6px 0 16px", background: "var(--ion-background-color)", borderBottom: "1px solid var(--app-divider)", flexShrink: 0, gap: 2 };
  const subTextStyle = { flex: 1, fontSize: "0.8rem", fontWeight: 400, color: "var(--ion-color-medium)" };
  const subBar = mode === "prefs" ? (
    <div className="notif-subbar" style={subBarStyle}>
      <span style={subTextStyle}>{t("Preferences")}</span>
    </div>
  ) : mode === "select" ? (
    <div className="notif-subbar notif-subbar-select" style={subBarStyle}>
      <IonCheckbox
        className="notif-select-all"
        aria-label={t("Select all")}
        checked={allSelected}
        indeterminate={selCount > 0 && !allSelected}
        onIonChange={toggleAll}
        style={{ flexShrink: 0 }}
      />
      <span style={{ flex: 1 }} />
      <IconBtn className="notif-mark-unread" icon={mailUnreadOutline} label={t("Mark as unread")} disabled={!selCount} onClick={() => withSelection((sel) => onSetRead?.(sel, false))} />
      <IconBtn className="notif-mark-read" icon={mailOpenOutline} label={t("Mark as read")} disabled={!selCount} onClick={() => withSelection((sel) => onSetRead?.(sel, true))} />
      <IconBtn
        className="notif-delete"
        icon={trashOutline}
        label={t("Delete")}
        color="var(--ion-color-danger)"
        disabled={!selCount}
        onClick={async () => {
          if (!selCount) return;
          if (await confirmAlert({ header: t("Delete selected notifications?"), message: `${selCount} ${selCount === 1 ? t("notification") : t("notifications")}` })) {
            await withSelection((sel) => onDelete?.(sel));
          }
        }}
      />
      <IconBtn className="notif-select-close" icon={closeOutline} label={t("Done")} onClick={exitSelect} size={24} />
    </div>
  ) : (
    <div className="notif-subbar" style={subBarStyle}>
      <span style={subTextStyle}>{t("Notifications")}</span>
      <IconBtn className="notif-edit" icon={createOutline} label={t("Select")} disabled={items.length === 0} onClick={() => setMode("select")} />
    </div>
  );

  const body = mode === "prefs" ? (
    <div className="notif-prefs" style={{ flex: 1, overflowY: "auto" }}>
      {prefGroups.map((g) => (
        <div key={g.key} className="notif-pref-group" style={{ padding: "14px 16px 8px", borderBottom: "1px solid var(--app-divider)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "1.05rem", fontWeight: 500, color: "var(--ion-text-color)" }}>{g.title}</div>
              {g.description && <div style={{ fontSize: "0.9rem", color: "var(--ion-color-medium)", lineHeight: 1.35, marginTop: 2 }}>{g.description}</div>}
            </div>
            {!g.toggles && (
              <IonToggle
                aria-label={g.title}
                checked={prefs[g.key] !== false}
                onIonChange={(e) => onPrefChange?.(g.key, e.detail.checked)}
                style={{ flexShrink: 0 }}
              />
            )}
          </div>
          {g.toggles && g.toggles.map((tg) => (
            <div key={tg.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 0" }}>
              <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--ion-color-medium)" }}>{tg.label}</span>
              <IonToggle
                aria-label={tg.label}
                checked={prefs[tg.key] !== false}
                onIonChange={(e) => onPrefChange?.(tg.key, e.detail.checked)}
                style={{ flexShrink: 0 }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  ) : items.length === 0 ? (
    <NoResults />
  ) : (
    <div style={{ flex: 1, overflowY: "auto" }}>
      <IonList lines="full" style={{ padding: 0, background: "transparent" }}>
        {items.map((n) => (
          <IonItem
            key={n.id}
            button
            detail={false}
            className={`notif-row${n.read ? "" : " is-unread"}${selected.has(n.id) ? " is-selected" : ""}`}
            onClick={() => (mode === "select" ? toggleOne(n.id) : onOpenItem?.(n))}
            style={{
              "--background": n.read ? "transparent" : "var(--ion-color-step-50)",
              "--min-height": "68px",
              "--padding-start": "16px",
              "--inner-padding-end": "12px",
            }}
          >
            {mode === "select" && (
              <IonCheckbox
                slot="start"
                aria-label={t("Select")}
                checked={selected.has(n.id)}
                style={{ pointerEvents: "none", marginInlineEnd: 14, flexShrink: 0 }}
              />
            )}
            <IonLabel style={{ whiteSpace: "normal" }}>{renderItem ? renderItem(n) : null}</IonLabel>
          </IonItem>
        ))}
      </IonList>
    </div>
  );

  return createPortal(<>
    {open && (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex, background: "rgba(0,0,0,0.3)" }} />
    )}
    <div
      className={`notif-drawer notif-mode-${mode}`}
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: zIndex + 1,
        width, maxWidth: "90vw",
        background,
        boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      {header}
      {subBar}
      {body}
    </div>
  </>, document.querySelector("ion-app") || document.body);
}
